import React from 'react'
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export default function TitleCommitmentServices() {
    const serviceCategories = [
        {
            category: "Title Commitment & Package Services",
            description:
                "Our Title Commitment and Package Preparation services provide structured back-office support for U.S. title agencies, underwriters, and settlement teams, ensuring precision, compliance, and closing readiness.",
            services: [
                {
                    name: "Title Commitment Preparation",
                    icon: "📄",
                    details:
                        "Accurate drafting of the Title Commitment based on completed title search and examination. Includes Schedule A, Schedule B-I (requirements), and Schedule B-II (exceptions) in full compliance with underwriter guidelines, ensuring all transaction-specific details are correctly reflected prior to issuance."
                },
                {
                    name: "Document Typing & Data Entry",
                    icon: "⌨️",
                    details:
                        "Precise entry of property details, borrower and lender information, vesting, and legal descriptions into standardized title templates. Documents are formatted according to client and underwriter specifications to maintain consistency, accuracy, and audit readiness."
                },
                {
                    name: "Chain of Title Review",
                    icon: "🔗",
                    details:
                        "Comprehensive examination of historical ownership transfers to confirm a clear and marketable chain of title. Verifies deed continuity, identifies gaps or discrepancies, and ensures the seller has legal authority to convey the property."
                },
                {
                    name: "Lien & Encumbrance Review",
                    icon: "📑",
                    details:
                        "Identification and documentation of open mortgages, judgments, tax liens, restrictions, and other encumbrances. Includes incorporation of payoff conditions and clearance requirements to reduce transactional risk before closing."
                },
                {
                    name: "Compliance & Supporting Documents Compilation",
                    icon: "📂",
                    details:
                        "Preparation and verification of essential supporting documents including CPL, tax certifications, bankruptcy searches, disclosures, wire instructions, and fee estimates. Ensures regulatory compliance and lender-specific standards are fully met, delivering a complete closing-ready title package."
                },
                {
                    name: "Quality Control & Final Review",
                    icon: "✅",
                    details:
                        "Multi-level quality review process to validate accuracy, completeness, and adherence to underwriter guidelines. Includes cross-checking legal descriptions, vesting details, loan information, and commitment requirements to minimize post-issuance corrections and prevent closing delays."
                },
            ],
        },
    ];

    const stats = [
        { value: "8+", label: "Core Services" },
        { value: "PA & NY", label: "Specialized Search" },
        { value: "100%", label: "Compliance Rate" },
        { value: "Zero", label: "Post-Closing Surprises" },
    ];

    return (
        <div className="bg-white">
            <Helmet>
                <meta charSet="utf-8" />
                <title>CityPulse LLC | Title Commitment Services</title>
                <link rel="canonical" href="https://citypulsellc.com/title-commitment-services" />
            </Helmet>

            {/* ── HERO ── */}
            <section className="relative min-h-[60vh] flex flex-col justify-center overflow-hidden bg-gray-950">

                {/* Background photo */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80&auto=format&fit=crop')`
                    }}
                />
                {/* Layered overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-950/85 via-gray-900/70 to-blue-900/50" />

                {/* Decorative blobs + dot grid */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-500/20 blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-indigo-500/10 blur-3xl" />
                    <div
                        className="absolute inset-0 opacity-10"
                        style={{
                            backgroundImage: `radial-gradient(circle, #fff 1px, transparent 1px)`,
                            backgroundSize: `32px 32px`,
                        }}
                    />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">

                    {/* Eyebrow badge */}
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-300">
                            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                            City Pulse Services
                        </span>
                    </div>

                    {/* Headline */}
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none mb-6 max-w-3xl">
                        Title Commitment
                        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300 mt-1">
                            Services
                        </span>
                    </h1>

                    {/* Accent divider */}
                    <div className="flex items-center gap-4 mb-6">
                        <div className="h-px w-16 bg-gradient-to-r from-blue-400 to-transparent" />
                        <div className="w-2 h-2 rounded-full bg-blue-400" />
                    </div>

                    {/* Description */}
                    <p className="text-lg sm:text-xl text-gray-300 max-w-2xl leading-relaxed mb-12">
                       Structured back-office services for U.S. title agencies: accurate commitments, compliant packages, and closing readiness.
                    </p>

                    {/* Stats row */}
                    {/* <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
                        {stats.map((stat, i) => (
                            <div
                                key={i}
                                className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl px-4 py-4 text-center hover:bg-white/10 transition-colors duration-200"
                            >
                                <div className="text-lg sm:text-2xl font-black text-white leading-tight">{stat.value}</div>
                                <div className="text-xs text-gray-400 mt-0.5 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div> */}

                    {/* Scroll hint */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
                        <span className="text-white text-xs uppercase tracking-widest">Scroll</span>
                        <div className="w-px h-8 bg-gradient-to-b from-white to-transparent animate-bounce" />
                    </div>
                </div>
            </section>

            {/* ── SERVICES GRID ── */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Section label */}
                    <div className="flex items-center gap-3 mb-3">
                        <div className="h-px w-10 bg-blue-500" />
                        <span className="text-xs font-bold uppercase tracking-widest text-blue-700">What We Handle</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                        Title Commitment & Package Services
                    </h2>
                    <p className="text-gray-500 mb-12 max-w-2xl">
                        Our Title Commitment and Package Preparation services provide structured back-office support for U.S. title agencies, underwriters, and settlement teams, ensuring precision, compliance, and closing readiness.
                    </p>

                    {serviceCategories.map((category, index) => (
                        <div key={index} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {category.services.map((service, idx) => (
                                <div
                                    key={idx}
                                    className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl border border-gray-100 hover:border-blue-200 transition-all duration-300 flex flex-col gap-4"
                                >
                                    {/* Icon + Name */}
                                    <div className="flex justify-start items-center gap-3">
                                        <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center text-xl group-hover:bg-blue-100 transition-colors">
                                            {service.icon}
                                        </div>
                                        <h3 className="text-base lg:text-lg 2xl:text-xl font-bold text-gray-900 leading-snug pt-1">
                                            {service.name}
                                        </h3>
                                    </div>

                                    {/* Divider */}
                                    <div className="h-px bg-gray-100 group-hover:bg-blue-100 transition-colors" />

                                    {/* Details */}
                                    {service.details && (
                                        <p className="text-sm lg:text-base text-gray-800 leading-relaxed flex-1">
                                            {service.details}
                                        </p>
                                    )}

                                    {/* Tag */}
                                    {/* <div className="self-start">
                                        <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full">
                                            Tax
                                        </span>
                                    </div> */}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* ── CTA SECTION ── */}
            <section className="relative py-20 overflow-hidden bg-gray-950">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-blue-500/20 blur-3xl -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-yellow-500/15 blur-3xl translate-y-1/2 -translate-x-1/4" />
                    <div
                        className="absolute inset-0 opacity-5"
                        style={{
                            backgroundImage: `radial-gradient(circle, #fff 1px, transparent 1px)`,
                            backgroundSize: `28px 28px`,
                        }}
                    />
                </div>

                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-400 mb-4">
                        Let's Work Together
                    </span>
                    <h2 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
                        Ready to Get Started?
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-blue-400 to-yellow-300 rounded-full mx-auto mb-6" />
                    <p className="text-lg text-gray-400 mb-10 max-w-xl mx-auto leading-relaxed">
                        Contact us today to learn how City Pulse can support your mortgage processing needs.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold py-3.5 px-8 rounded-xl transition-all duration-200 hover:scale-105 shadow-xl shadow-primary/30 text-sm"
                        >
                            Contact Us
                            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Link>
                        <Link
                            to="/services"
                            className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-gray-300 hover:text-white font-semibold py-3.5 px-8 rounded-xl transition-all duration-200 text-sm"
                        >
                            View All Services
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}
